<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import PipelinesAPI from 'custom/dashboard/api/pipelines';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PipelineIconPicker from './components/PipelineIconPicker.vue';
import PipelineStages from './components/PipelineStages.vue';
import { PIPELINES_CHANGED } from '../crm/pipelineEvents';

// How each funnel names the two ways a card closes (Pipeline#status_labels): a lead is
// "Locado" or "Perdido", a ticket "Resolvido" or "Cancelado". A blank one shows the default.
const CLOSED_STATUSES = ['resolved', 'canceled'];

const { t } = useI18n();
const route = useRoute();

const pipeline = ref(null);
const stages = ref([]);
const name = ref('');
const icon = ref('');
const labels = ref({});
const isSaving = ref(false);

const fill = record => {
  pipeline.value = record;
  name.value = record.name;
  icon.value = record.icon;
  labels.value = Object.fromEntries(
    CLOSED_STATUSES.map(status => [
      status,
      record.status_labels?.[status] || '',
    ])
  );
};

const isChanged = computed(
  () =>
    name.value.trim() !== pipeline.value.name ||
    icon.value !== pipeline.value.icon ||
    CLOSED_STATUSES.some(
      status =>
        labels.value[status].trim() !==
        (pipeline.value.status_labels?.[status] || '')
    )
);

const save = async () => {
  // The open status keeps whatever name it has; the blank ones fall back to the default.
  const statusLabels = {
    ...(pipeline.value.status_labels?.open && {
      open: pipeline.value.status_labels.open,
    }),
    ...Object.fromEntries(
      CLOSED_STATUSES.map(status => [
        status,
        labels.value[status].trim(),
      ]).filter(([, label]) => label)
    ),
  };
  isSaving.value = true;
  try {
    const { data } = await PipelinesAPI.update(pipeline.value.id, {
      pipeline: {
        name: name.value.trim(),
        icon: icon.value,
        status_labels: statusLabels,
      },
    });
    fill(data);
    emitter.emit(PIPELINES_CHANGED);
    useAlert(t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.SAVED'));
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  try {
    const { pipelineId } = route.params;
    const [pipelineResponse, stagesResponse] = await Promise.all([
      PipelinesAPI.show(pipelineId),
      PipelinesAPI.getStages(pipelineId),
    ]);
    fill(pipelineResponse.data);
    stages.value = stagesResponse.data;
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.LOAD_ERROR'));
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="!pipeline"
    :loading-message="$t('CARDS.PIPELINES_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="pipeline?.name || ''"
        description=""
        :back-button-label="$t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.BACK')"
      />
    </template>

    <template #body>
      <div
        v-if="pipeline"
        class="grid max-w-5xl gap-8 lg:grid-cols-2"
        data-testid="pipeline-edit-page"
      >
        <form class="flex flex-col gap-6" @submit.prevent="save">
          <section class="flex flex-col gap-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.DETAILS') }}
            </h3>
            <div class="flex items-end gap-2">
              <PipelineIconPicker v-model="icon" />
              <Input
                v-model="name"
                :label="$t('CARDS.PIPELINES_SETTINGS.FORM.NAME')"
                class="flex-1"
                data-testid="pipeline-name"
              />
            </div>
          </section>
          <section class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ $t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.CLOSE_NAMES') }}
              </h3>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ $t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.CLOSE_NAMES_HINT') }}
              </p>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <Input
                v-for="status in CLOSED_STATUSES"
                :key="status"
                v-model="labels[status]"
                :label="$t(`CARDS.PIPELINES_SETTINGS.LABEL.${status}`)"
                :placeholder="$t(`CARDS.STATUS.${status}`)"
                :data-testid="`pipeline-label-${status}`"
              />
            </div>
          </section>
          <div>
            <Button
              type="submit"
              :label="$t('CARDS.PIPELINES_SETTINGS.EDIT_PAGE.SAVE')"
              :disabled="!isChanged || !name.trim()"
              :is-loading="isSaving"
              data-testid="pipeline-save"
            />
          </div>
        </form>

        <PipelineStages
          v-model="stages"
          :pipeline-id="pipeline.id"
          :locked="pipeline.system_managed"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
